import React from 'react';
import { Box, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { TFunction } from 'i18next';
import { getNodeFocusRing, getNodeHoverBg, segmentKey } from './StatementXmlPreview.utils';
import type { StatementNodeData, StatementSegment } from './types';

type StatementXmlPreviewInlineProps = {
  segments: StatementSegment[];
  maxLines?: number;
  sx?: SxProps<Theme>;
  showGlossaryCodeOnHover: boolean;
  onNodeClick?: (node: StatementNodeData) => void;
  t: TFunction;
};

export const StatementXmlPreviewInline: React.FC<StatementXmlPreviewInlineProps> = ({
  segments,
  maxLines,
  sx,
  showGlossaryCodeOnHover,
  onNodeClick,
  t,
}) => {
  return (
    <Box
      sx={{
        whiteSpace: 'pre-wrap',
        ...(maxLines
          ? {
              display: '-webkit-box',
              WebkitLineClamp: maxLines,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }
          : null),
        ...sx,
      }}
    >
      {segments.map((segment, index) => {
        if (segment.kind === 'text') return <React.Fragment key={segmentKey(segment, index)}>{segment.text}</React.Fragment>;

        const node = segment.node;
        const showTooltip = showGlossaryCodeOnHover && node.type === 'GlossaryStatementNode' && !!node.code;

        const content = (
          <Box
            component="span"
            role={onNodeClick ? 'button' : undefined}
            tabIndex={onNodeClick ? 0 : undefined}
            onClick={onNodeClick ? () => onNodeClick(node) : undefined}
            data-node-type={node.type}
            data-node-id={node.id}
            data-node-code={node.code}
            data-node-group-id={node.groupId}
            data-node-has-variable={node.hasVariable}
            data-node-is-edited={node.isEdited}
            sx={(theme) => ({
              borderRadius: 0.75,
              px: 0.25,
              transition: 'background-color 120ms ease-in-out, box-shadow 120ms ease-in-out',
              cursor: onNodeClick ? 'pointer' : 'default',
              '&:hover': { backgroundColor: getNodeHoverBg(theme, node) },
              '&:focus-visible': {
                outline: 'none',
                backgroundColor: getNodeHoverBg(theme, node),
                boxShadow: `0 0 0 2px ${getNodeFocusRing(theme, node)}`,
              },
            })}
          >
            {node.text}
          </Box>
        );

        if (!showTooltip) return <React.Fragment key={segmentKey(segment, index)}>{content}</React.Fragment>;

        const tooltip = (
          <Box>
            <Box component="div">{t('clp.tooltipCode', { code: node.code })}</Box>
            {node.isEdited === true && <Box component="div">{t('clp.edited')}</Box>}
          </Box>
        );

        return (
          <Tooltip key={segmentKey(segment, index)} title={tooltip} arrow placement="top">
            {content}
          </Tooltip>
        );
      })}
    </Box>
  );
};
